import React, { memo, useEffect, useState } from 'react';
import {
  Edit,
  SimpleForm,
  TextInput,
  SelectInput,
  required,
  SaveButton,
  ImageInput,
  Toolbar,
  LongTextInput,
  NumberInput,
  SelectArrayInput,
  GET_MANY,
} from 'react-admin';
import dataProvider from '../../providers/dataProvider';
import { paymentTypeSelectOptions } from '../../constants/paymentType';
import uploadService from '../../utils/upload';
import removeUnnecessaryProductFields from '../../utils/removeUnnecessaryProductFields';
import RemoveButton from '../../components/RemoveButton/RemoveButton';
import ImgField from '../../components/ImgField/ImgField';
import Loading from '../../components/Loading/Loading';
import DeleteDialog from './components/DeleteDialog';
import PaymentTypeAdditionalField from './components/PaymentTypeAdditionalField';

const uploadImg = (img) => {
  if (img && typeof img === 'object') {
    const formData = new FormData();
    formData.append('file', img.rawFile);
    return uploadService(formData);
  }
  return Promise.resolve({ data: { fileName: img } });
};

const parseOptions = (v) =>
  v?.map((i) => {
    if (typeof i === 'number') {
      return i;
    }
    return i.programId;
  });

const ToolBar = (props) => {
  const [isOpenDeleteModal, changeIsOpenDeleteModal] = useState(false);
  const openDeleteModal = () => {
    changeIsOpenDeleteModal(true);
  };
  const closeDeleteModal = () => {
    changeIsOpenDeleteModal(false);
  };

  return (
    <>
      <Toolbar
        {...props}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          background: 'none',
        }}
      >
        <SaveButton {...props} label="save" submitOnEnter={false} />
        <RemoveButton variant="contained" deleteExercise={openDeleteModal} />
      </Toolbar>
      <DeleteDialog
        record={props.record}
        isOpen={isOpenDeleteModal}
        handleClose={closeDeleteModal}
        basePath={props.basePath}
        resource={props.resource}
        redirect="list"
      />
    </>
  );
};

const MultipleProductIdsInput = ({ externalProductIds, setExternalProductIds }) => {
  const [currentInput, setCurrentInput] = useState('');

  const handleKeyDown = (e) => {
    if (e.key === 'Tab' && currentInput.trim()) {
      e.preventDefault();
      const newIds = [...externalProductIds, currentInput.trim()];
      setExternalProductIds(newIds);
      setCurrentInput('');
    }
  };

  const handleChange = (e) => {
    setCurrentInput(e.target.value);
  };

  return (
    <div>
      <div style={{ marginBottom: '8px' }}>
        {externalProductIds.map((id, index) => (
          <span
            key={`$sc-${id}`}
            style={{
              background: '#e0e0e0',
              padding: '4px 8px',
              borderRadius: '12px',
              fontSize: '10px',
              margin: '26px 4px 4px 0',
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            {id}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                const newIds = externalProductIds.filter((_, idx) => idx !== index);
                setExternalProductIds(newIds);
              }}
              style={{
                border: 'none',
                background: 'none',
                marginLeft: '4px',
                cursor: 'pointer',
                padding: '0 4px',
              }}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <input
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        value={currentInput}
        style={{
          marginTop: '0',
          border: 'none',
          borderBottom: '1px solid #ccc',
          fontSize: '16px',
          padding: '8px 0',
          width: '40%',
          outline: 'none',
        }}
        placeholder="Type a Product ID and press Tab"
      />
    </div>
  );
};

const EditProductForm = ({ record, ...props }) => {
  const [isLoading, changeIsLoading] = useState(false);
  const [externalProductIds, setExternalProductIds] = useState(
    record.externalProductId ? record.externalProductId.split(',') : [],
  );
  const [options, changeOptions] = useState([]);

  useEffect(() => {
    dataProvider(GET_MANY, 'admin/products/programs').then((res) => changeOptions(res.data));
  }, []);

  const save = (data, redirect) => {
    changeIsLoading(true);
    const { picture, ...rawData } = data;
    uploadImg(picture)
      .then((res) => {
        const newData = removeUnnecessaryProductFields({
          ...rawData,
          picture: res.data.fileName,
          externalProductId: externalProductIds.join(','),
          programs: parseOptions(rawData.programs),
        });

        props.save(newData, redirect);
      })
      .finally(() => {
        changeIsLoading(false);
      });
  };

  return (
    <>
      <Loading open={isLoading} />
      <SimpleForm {...props} record={record} save={save} toolbar={<ToolBar />}>
        <TextInput source="name" label="Product Name" validate={required()} />
        <TextInput source="link" label="Checkout Link" />
        <TextInput source="cancelUrl" label="Cancel URL" />
        <MultipleProductIdsInput
          source="externalProductId"
          label="Product IDs"
          // value={externalProductIds.join(',')}
          externalProductIds={externalProductIds}
          setExternalProductIds={setExternalProductIds}
        />
        <SelectInput source="paymentType" choices={paymentTypeSelectOptions} />
        <NumberInput source="price" label="Price, $" validate={required()} />
        <PaymentTypeAdditionalField />
        <TextInput
          source="stripePriceId"
          label="Stripe Price Id"
          helperText={
            <span>
              Required field for Stripe Products, not needed for Samcart
              <br />
              if isPaymentPlan is selected should be subscription stripe price id
            </span>
          }
        />
        <NumberInput source="upsellPrice" label="Upsell Price, $" />
        <TextInput source="upsellStripePriceId" label="Upsell Stripe Price Id" />
        {options.length ? (
          <SelectArrayInput
            source="programs"
            choices={options}
            validate={required()}
            parse={parseOptions}
            format={parseOptions}
          />
        ) : null}
        <LongTextInput source="description" label="Description" validate={required()} />
        <ImageInput
          source="picture"
          label="Add Product Photo"
          accept="image/*"
          validate={required()}
          format={(v) => (typeof v === 'string' ? { picture: v } : v)}
        >
          <ImgField width="100" height="100" source="picture" />
        </ImageInput>
      </SimpleForm>
    </>
  );
};

const EditProduct = (props) => {
  return (
    <Edit {...props}>
      <EditProductForm />
    </Edit>
  );
};

export default memo(EditProduct);
